import { useCallback } from 'react';
import type { FC } from 'react';
import { useSelector } from 'react-redux';
import { ListItem } from '@interstate/components/List';
import { Box } from '@interstate/components/Box';
import { CheckBox } from '@interstate/components/CheckBox';
import { TextArea } from '@interstate/components/TextArea';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { DisplayItemKey } from '../constants';
import { MAX_DISCLAIMER_LENGTH } from '../constants';

const key: DisplayItemKey = 'customDisclaimer';

const DATE_TOKEN_PLACEHOLDER =
    'Use {date} to insert the print date. Example: "This offer is as of {date} and is subject to lender approval."';
const DATE_AND_TIME_TOKEN_PLACEHOLDER =
    'Use {date} to insert the print date and {time} to insert the print time. Example: "This offer is as of {date} at {time} and is subject to lender approval."';

type CustomDisclaimerFieldProps = {
    isActive: boolean;
    value: string;
    onDisplayActiveChange: (displayKey: DisplayItemKey, checked: boolean) => void;
    onDisplayValueChange: (displayKey: DisplayItemKey, newDisplayValue: string | undefined) => void;
};

const CustomDisclaimerField: FC<CustomDisclaimerFieldProps> = ({
    isActive,
    value,
    onDisplayActiveChange,
    onDisplayValueChange
}) => {
    const isDealerTimeZoneDisclaimerEnabled = useSelector(featureToggleSelectors.isDealerTimeZoneDisclaimerEnabled);

    const handleCheckboxChange = useCallback(
        (e: { target: { value: { checked: boolean } } }) => {
            onDisplayActiveChange(key, e.target.value.checked);
        },
        [onDisplayActiveChange]
    );

    const handleTextChange = useCallback(
        (e: { target: { value: string } }) => {
            onDisplayValueChange(key, e.target.value);
        },
        [onDisplayValueChange]
    );

    return (
        <ListItem key={key} id={key}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        height: '64px'
                    }}
                >
                    <CheckBox
                        value={key}
                        checked={isActive}
                        onChange={handleCheckboxChange}
                        data-testid={`display-tab-checkbox-${key}`}
                    />
                    <Box>Customized Disclaimer</Box>
                </Box>
                {isActive && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: 1 }}>
                        <TextArea
                            name="customDisclaimerText"
                            value={value}
                            onChange={handleTextChange}
                            placeholder={
                                isDealerTimeZoneDisclaimerEnabled ? DATE_AND_TIME_TOKEN_PLACEHOLDER : DATE_TOKEN_PLACEHOLDER
                            }
                            data-testid="custom-disclaimer-textarea"
                            rows={4}
                            maxLength={MAX_DISCLAIMER_LENGTH}
                            sx={{ caretColor: 'auto' }}
                            hasError={!value.trim()}
                            errorMessage="This field cannot be empty."
                        />
                    </Box>
                )}
            </Box>
        </ListItem>
    );
};

export default CustomDisclaimerField;
